import {
  useState,
  useCallback,
  useMemo,
  createContext,
  useContext,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { KeyLocalStore } from "~/common/constants";
import { ClientAuthMeRes, UserSessionDto } from "~/dto/auth.dto";

interface IAuthStore {
  isFirstLoading: boolean;
  logged: boolean;
  userInfo: UserSessionDto;
  tenantId: string;
  setUserInfo: (userInfo: UserSessionDto) => void;
  authenticate: () => Promise<void>;
  logout: () => void;
}

const AuthStoreContext = createContext<IAuthStore | undefined>(undefined);

const AuthStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const [isFirstLoading, setIsFirstLoading] = useState(false);
  const [logged, setLogged] = useState(
    localStorage.getItem(KeyLocalStore.accessToken) ? true : false
  );
  const [userInfo, setUserInfo] = useState<UserSessionDto>(undefined);
  const [tenantId, setTenantId] = useState<string>();

  useEffect(() => {
    const tenantId = localStorage.getItem(KeyLocalStore.tenantId);
    if (tenantId) {
      setTenantId(tenantId);
    }
  }, []);

  const authenticate = useCallback(async () => {
    setIsFirstLoading(true);
    try {
      const check = localStorage.getItem(KeyLocalStore.accessToken)
        ? true
        : false;
      if (check) {
        setLogged(true);
      }
    } catch (error) {
      setLogged(false);
    }
    setIsFirstLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(KeyLocalStore.accessToken);
    localStorage.removeItem(KeyLocalStore.tenantId);
    setLogged(false);
    setUserInfo(undefined);
    navigate("/");
  }, [navigate]);

  const values = useMemo(
    () => ({
      isFirstLoading,
      logged,
      userInfo,
      setUserInfo,
      authenticate,
      logout,
      tenantId,
    }),
    [authenticate, isFirstLoading, logged, logout, userInfo, tenantId]
  );

  return (
    <AuthStoreContext.Provider value={values}>
      {children}
    </AuthStoreContext.Provider>
  );
};

const useAuthStore = () => {
  const context = useContext(AuthStoreContext);
  if (context === undefined) {
    throw new Error(
      "useAuthStore hook must be used with a AuthStoreContext component"
    );
  }
  return context;
};

export { AuthStoreProvider, useAuthStore };
